/**
 * Loading, empty and error states.
 *
 * The brief is explicit that these are DESIGNED states, not spinners: "the states
 * exist in Figma; build them, not spinners." The skeleton geometry follows
 * `SkeletonTable` / `Table/Empty` (page 02, node 12:10921) — shimmering blocks in
 * `bg/subtle` at the row height the real content will occupy, so the layout does
 * not jump when data lands.
 *
 * `Alert` has no Figma component. It is authored from the `status/*` token pairs
 * the design already uses for the same meanings, and it is a recorded judgment:
 * login needs somewhere to put `INVALID_CREDENTIALS` and `ACCOUNT_LOCKED`, and the
 * alternative — a bare red sentence — is what "designed error states" was written
 * to prevent.
 */

import type { ReactNode } from 'react';
import { CircleAlert, Inbox, TriangleAlert } from 'lucide-react';
import { cn } from '../../lib/cn';

/* ─────────────────────────────────────────────────────────────── skeletons ── */

export function SkeletonBlock({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded-sm bg-subtle motion-reduce:animate-none', className)}
    />
  );
}

/**
 * A skeleton for a list of rows. `aria-busy` + a polite live label mean a screen
 * reader is told the region is loading rather than reading a wall of nothing.
 */
export function SkeletonRows({
  rows = 5,
  label = 'Loading…',
  className,
}: {
  rows?: number;
  label?: string;
  className?: string;
}): React.JSX.Element {
  return (
    <div aria-busy="true" aria-live="polite" aria-label={label} className={cn('flex flex-col', className)}>
      {Array.from({ length: rows }, (_unused, index) => (
        <div
          key={index}
          className="crf-register-row flex items-center gap-3 border-b border-line last:border-b-0"
        >
          <SkeletonBlock className="h-4 flex-1" />
          <SkeletonBlock className="h-5 w-28 rounded-pill" />
          <SkeletonBlock className="h-5 w-10 rounded-pill" />
          <SkeletonBlock className="size-6 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── empty ── */

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-4 py-10 text-center', className)}>
      <span className="mb-1 inline-flex size-10 items-center justify-center rounded-full bg-subtle text-fg-muted">
        {icon ?? <Inbox aria-hidden="true" className="size-5" />}
      </span>
      <p className="text-body font-medium text-fg">{title}</p>
      {description === undefined ? null : (
        <p className="max-w-sm text-label text-fg-secondary">{description}</p>
      )}
      {action}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── alert ── */

export type AlertTone = 'danger' | 'warning' | 'info';

const ALERT_TONES: Record<AlertTone, { box: string; Icon: typeof CircleAlert }> = {
  danger: { box: 'bg-danger-bg text-danger-fg', Icon: CircleAlert },
  warning: { box: 'bg-warn-bg text-warn-fg', Icon: TriangleAlert },
  info: { box: 'bg-info-bg text-info-fg', Icon: CircleAlert },
};

export function Alert({
  tone = 'danger',
  title,
  children,
  className,
}: {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  className?: string;
}): React.JSX.Element {
  const { box, Icon } = ALERT_TONES[tone];
  return (
    <div
      role="alert"
      className={cn('flex w-full items-start gap-2 rounded-sm px-3 py-2.5', box, className)}
    >
      <Icon aria-hidden="true" className="mt-px size-4 shrink-0" />
      <div className="min-w-0 text-label">
        {title === undefined ? null : <p className="font-medium">{title}</p>}
        {children}
      </div>
    </div>
  );
}
